import { useState, useRef, useEffect } from 'react';
import { Send, Loader2, Globe } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

interface ChatInputProps {
  onSendMessage: (message: string, performWebSearch?: boolean) => void;
  isLoading: boolean;
  placeholder?: string;
}

export const ChatInput = ({ onSendMessage, isLoading, placeholder = "Ask about this lead..." }: ChatInputProps) => {
  const [message, setMessage] = useState('');
  const [webSearchEnabled, setWebSearchEnabled] = useState(false);
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleSubmit = () => {
    const trimmedMessage = message.trim();
    if (trimmedMessage && !isLoading) {
      onSendMessage(trimmedMessage, webSearchEnabled);
      setMessage('');
      // Reset textarea height
      if (textareaRef.current) {
        textareaRef.current.style.height = 'auto';
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  // Auto-resize textarea
  useEffect(() => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      const scrollHeight = textareaRef.current.scrollHeight;
      textareaRef.current.style.height = `${Math.min(scrollHeight, 120)}px`;
    }
  }, [message]);

  return (
    <div className="border-t bg-gradient-to-t from-muted/30 to-background p-4">
      <div className="flex items-end gap-2">
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                type="button"
                variant={webSearchEnabled ? "default" : "outline"}
                size="icon"
                onClick={() => setWebSearchEnabled(!webSearchEnabled)}
                disabled={isLoading}
                className={cn(
                  "shrink-0 transition-all",
                  webSearchEnabled && "bg-gradient-to-br from-green-500 to-green-600 shadow-lg shadow-green-500/20"
                )}
              >
                <Globe className={cn(
                  "h-4 w-4 transition-all",
                  webSearchEnabled ? "animate-pulse" : "opacity-50"
                )} />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p className="text-xs">
                {webSearchEnabled ? "Web search enabled" : "Web search disabled"}
              </p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>

        <Textarea
          ref={textareaRef}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={placeholder}
          disabled={isLoading}
          className="min-h-[40px] max-h-[120px] resize-none"
          rows={1}
        />
        <Button
          onClick={handleSubmit}
          disabled={!message.trim() || isLoading}
          size="icon"
          className="shrink-0 bg-gradient-to-br from-primary to-primary/80 shadow-md hover:shadow-lg transition-all"
        >
          {isLoading ? (
            <Loader2 className="w-4 h-4 animate-spin" />
          ) : (
            <Send className="w-4 h-4" />
          )}
        </Button>
      </div>
      
      <div className="flex items-center justify-between mt-2">
        <p className="text-xs text-muted-foreground">
          Press Enter to send, Shift+Enter for new line
        </p>
        {webSearchEnabled && (
          <div className="flex items-center gap-2 text-xs text-green-600 dark:text-green-400">
            <div className="h-1.5 w-1.5 rounded-full bg-green-500 animate-pulse" />
            <span className="font-medium">Web search active</span>
          </div>
        )}
      </div>
    </div>
  );
};
